import React from 'react';
import Moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/pro-light-svg-icons';
import {
  getReMeetingCounts,
  getReMeetingSummaries,
  type ReMeetingSummary,
} from '../../../../../api/re-meetings.api';
import type {
  MessageInfo,
  MessageInfoChild,
} from '../../../../../api/cmail/cmail.dto';

type CmailMessage = MessageInfo | MessageInfoChild;

/** Only inbound Risk Engineering request emails can have a follow-up meeting,
 *  so nothing else asks chat-agent. The email view's thread payload doesn't
 *  always carry a direction, so only an explicitly sent or draft email is
 *  ruled out. */
export const canHaveMeetingSummary = (message: CmailMessage): boolean => {
  const direction = (
    message.emailDirection as string | undefined
  )?.toUpperCase();
  return (
    message.msgCategory?.toUpperCase() === 'RISKENG' &&
    direction !== 'SENT' &&
    direction !== 'DRAFT'
  );
};

export const meetingButtonLabel = (count: number): string =>
  count > 1 ? `Meeting summaries (${count})` : 'Meeting summary';

/** "Oct 9, 2026 2:00 PM · 30 min", or what is known of it. */
export const formatMeetingWhen = (
  start: string | null,
  end: string | null
): string => {
  if (!start) return 'Date unknown';
  const when = Moment(start).format('lll');
  if (!end) return when;
  const minutes = Math.round(Moment(end).diff(Moment(start), 'minutes', true));
  return minutes > 0 ? `${when} · ${minutes} min` : when;
};

const NOTE_STATUS_LABELS: Record<ReMeetingSummary['noteStatus'], string> = {
  posted: "Added to the account's notes",
  pending: "Being added to the account's notes",
  failed: "Could not be added to the account's notes",
};

export const matchedByLabel = (matchedBy: string): string =>
  matchedBy === 'name'
    ? "Linked to this email by the participant's name."
    : "Linked to this email by the participant's email.";

export const noteStatusLabel = (status: string): string =>
  NOTE_STATUS_LABELS[status as ReMeetingSummary['noteStatus']] ?? '';

const summariesKey = (msgId: string) => ['re-meeting-summaries', msgId];

const fetchSummaries = (msgId: string) =>
  getReMeetingSummaries({ params: { msgId } }).then(
    (resp) => resp.data.meetings
  );

export const useReMeetingSummaries = (message: CmailMessage) =>
  useQuery({
    queryKey: summariesKey(message.msgId),
    queryFn: () => fetchSummaries(message.msgId),
    enabled: Boolean(message.msgId) && canHaveMeetingSummary(message),
    staleTime: 60 * 1000,
  });

/** The emails on a Concierge list page that could have a meeting summary —
 *  only inbound Risk Engineering mail, so other tabs make no request. */
export const meetingLookupIds = (
  messages: readonly { msgId?: string; msgCategory?: string }[],
  mailboxType?: string
): string[] => {
  if (mailboxType !== 'INBOX') return [];
  return messages
    .filter((m) => m.msgId && m.msgCategory?.toUpperCase() === 'RISKENG')
    .map((m) => m.msgId as string);
};

/** Meeting-summary counts for one list page, in a single request. */
export const useReMeetingCounts = (msgIds: readonly string[]) =>
  useQuery({
    queryKey: ['re-meeting-counts', [...msgIds].sort()],
    queryFn: () =>
      getReMeetingCounts({ data: { msgIds: [...msgIds] } }).then(
        (resp) => resp.data.counts
      ),
    enabled: msgIds.length > 0,
    staleTime: 60 * 1000,
  });

const MeetingSection = ({
  label,
  items,
}: Readonly<{ label: string; items: readonly React.ReactNode[] }>) => {
  if (items.length === 0) return null;
  return (
    <Box>
      <Text fw={600} size="sm">
        {label}
      </Text>
      <ul>
        {items.map((item, i) => (
          // Items are plain text without ids; order is stable per meeting.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            <Text size="sm" component="div">
              {item}
            </Text>
          </li>
        ))}
      </ul>
    </Box>
  );
};

const MeetingSummaryCard = ({
  meeting,
}: Readonly<{ meeting: ReMeetingSummary }>) => {
  const requester = meeting.requesterName || meeting.participantEmail;
  return (
    <Stack gap="xs">
      <Title order={5}>{meeting.topic}</Title>
      <Text size="sm" c="dimmed">
        {formatMeetingWhen(meeting.meetingStart, meeting.meetingEnd)} · Hosted
        by {meeting.hostEmail} · With {requester}
      </Text>
      <Text size="sm">{meeting.summary}</Text>
      <MeetingSection label="Key points" items={meeting.keyPoints} />
      <MeetingSection
        label="Action items"
        items={meeting.actionItems.map((a) => (
          <>
            <Badge size="xs" variant="light" mr={6}>
              {a.owner}
            </Badge>
            {a.description}
          </>
        ))}
      />
      {meeting.nextSteps && (
        <Text size="sm">
          <strong>Next steps:</strong> {meeting.nextSteps}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {noteStatusLabel(meeting.noteStatus)}. Summarised automatically from the
        Zoom transcript. {matchedByLabel(meeting.matchedBy)}
      </Text>
    </Stack>
  );
};

/** The summaries of one request email's follow-up meetings. Loads them when
 *  opened (the list icon has only a count), or reuses the email view's copy. */
export const MeetingSummaryModal = ({
  msgId,
  opened,
  onClose,
}: Readonly<{ msgId: string; opened: boolean; onClose: () => void }>) => {
  const {
    data: meetings = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: summariesKey(msgId),
    queryFn: () => fetchSummaries(msgId),
    enabled: opened && Boolean(msgId),
    staleTime: 60 * 1000,
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={meetingButtonLabel(Math.max(meetings.length, 1))}
      size="lg"
      // Above the console header, like the other Concierge modals.
      zIndex={9999}
    >
      {isLoading && <Loader size="sm" />}
      {!isLoading && (isError || meetings.length === 0) && (
        <Text size="sm" c="dimmed">
          {isError
            ? "The meeting summary couldn't be loaded. Please try again later."
            : 'No meeting summary is available for this email yet.'}
        </Text>
      )}
      {!isLoading && !isError && meetings.length > 0 && (
        <Stack gap="lg">
          {meetings.map((meeting) => (
            <MeetingSummaryCard key={meeting.id} meeting={meeting} />
          ))}
        </Stack>
      )}
    </Modal>
  );
};

/** Shown on a Risk Engineering request email once a follow-up Zoom meeting
 *  has been summarised; opens the summary. Renders nothing otherwise. */
export const MeetingSummaryButton = ({
  message,
}: Readonly<{ message: CmailMessage }>) => {
  const [open, setOpen] = React.useState(false);
  const { data: meetings = [] } = useReMeetingSummaries(message);

  if (meetings.length === 0) return null;

  return (
    <>
      <Button
        variant="light"
        size="compact-sm"
        leftSection={<FontAwesomeIcon icon={faVideo} />}
        // The header row squeezes its children; the label must stay readable.
        style={{ flexShrink: 0 }}
        onClick={() => setOpen(true)}
      >
        {meetingButtonLabel(meetings.length)}
      </Button>
      <MeetingSummaryModal
        msgId={message.msgId}
        opened={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

/** The Concierge list's marker for a request email with a meeting summary;
 *  opens the summary without opening the email. */
export const MeetingSummaryListIcon = ({
  msgId,
  count,
}: Readonly<{ msgId: string; count: number }>) => {
  const [open, setOpen] = React.useState(false);
  if (count <= 0) return null;
  return (
    <>
      <Tooltip label={meetingButtonLabel(count)} withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={meetingButtonLabel(count)}
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon={faVideo} />
        </ActionIcon>
      </Tooltip>
      <MeetingSummaryModal
        msgId={msgId}
        opened={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
